/**
 * Saving a stored file (broadcast capture, run artifact) to the user's disk.
 *
 * A plain `<a href download>` does not work for our files: the `download` attribute is ignored for
 * cross-origin URLs, so the browser navigates and renders the PDF/PNG in a tab instead of saving
 * it. The file is therefore fetched and saved from a blob URL, which also lets us choose the name.
 *
 * That fetch only succeeds when the storage bucket answers CORS. The request path is
 * portal → file-cdn → 302 → R2, and a cross-origin redirect makes the browser send `Origin: null`
 * on the final hop — so the bucket policy must allow `*`, not a list of portal domains
 * (`signagecloud-dev` / `signagecloud-prd` do; see optisigns-infras #250). When the read is refused
 * anyway, the file is opened in a tab so the user still gets it.
 *
 * Same helper as scio3's `lib/download-file.ts` (scio3#2149), so both products save files the same
 * way. Deliberately framework-free — no React, stores or toasts. Callers own the wording.
 */

/** What happened to a download, so the caller can tell the user. */
export const DOWNLOAD_OUTCOME = {
  /** Fetched and saved under the requested name. */
  SAVED: "saved",
  /** The storage host refused the read; the file was opened in a new tab instead. */
  OPENED: "opened",
} as const;
export type DownloadOutcome =
  (typeof DOWNLOAD_OUTCOME)[keyof typeof DOWNLOAD_OUTCOME];

/** Characters Windows and macOS reject in file names. */
const RESERVED_FILE_NAME_CHARS = /[\\/:*?"<>|]/g;

/** Save a Blob to disk through a transient `<a download>`. */
export function saveBlob(blob: Blob, fileName: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = blobUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(blobUrl);
}

/**
 * Fetch `url` and save it as `fileName`; if the storage host refuses the cross-origin read, open
 * it in a new tab instead. Never throws — a download button must not break the page it sits on.
 *
 * A non-2xx response is treated as a refusal, not a file: an expired signature answers 403, and
 * saving that error body under a `.pdf` name would hand the user a broken file.
 */
export async function downloadFromUrl(
  url: string,
  fileName: string,
): Promise<DownloadOutcome> {
  try {
    const response = await fetch(url, { credentials: "omit" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    saveBlob(await response.blob(), fileName);
    return DOWNLOAD_OUTCOME.SAVED;
  } catch {
    // Expected when the bucket has no CORS policy — not an error worth logging per click.
    // `noopener,noreferrer` keeps the opened page from reaching back into this tab.
    window.open(url, "_blank", "noopener,noreferrer");
    return DOWNLOAD_OUTCOME.OPENED;
  }
}

/** Replace characters Windows and macOS reject in file names with `_`. */
export function toSafeFileName(name: string): string {
  return name.replace(RESERVED_FILE_NAME_CHARS, "_");
}

/** Lower-case extension from a URL's path, ignoring the query string; `undefined` if there is none. */
export function extensionFromUrl(url: string): string | undefined {
  return url
    .split("?")[0]
    .match(/\.([a-z0-9]{2,5})$/i)?.[1]
    .toLowerCase();
}
